import { StacksBySite } from '@pubstack/common/src/stack'
import { FunctionComponent, ReactNode, useMemo } from 'react'
import Button from '~/components/Button'
import { Flyout } from '~/components/Flyout'
import { TrackingLabel } from '~/components/layout/ProductTrackingIntegration'

export type RuleDetail = { label: string; value: ReactNode }

type Props = {
  /** The rule's name, which titles the flyout. */
  name: string
  /** The rule's own settings, in the order the table shows its columns. */
  details: RuleDetail[]
  /** Which stacks run this rule, keyed by media name — the same shape a bidder rule's `usedInStacks` has. */
  stacksUse?: StacksBySite
  onEdit?: () => void
  /** Absent on a Pubstack-owned rule, which is what replaces the Edit button with the reason it is missing. */
  editDisabledReason?: string
  editTrackingLabel: TrackingLabel
}

/**
 * What a lazy loading or refresh rule is, and where it is running — the read-only view both rule lists
 * lacked, where the only way to see a rule's settings was to open the form that changes them.
 *
 * Modelled on `ViewBidderRuleFlyout`, deliberately: a rule is a rule, and a publisher who has read one of
 * these should not have to learn a second layout. Same title-and-details shape, same "Used by" table with
 * its count as the caption, same Edit in the footer.
 *
 * The settings themselves are passed in rather than derived here. Lazy loading counts viewports and refresh
 * counts seconds; the two have no field in common beyond the name, and a component that tried to know both
 * would be a switch on which rule it was handed.
 */
export const RuleDetailsFlyout: FunctionComponent<Props> = ({ name, details, stacksUse, onEdit, editDisabledReason, editTrackingLabel }) => {
  // How much is riding on this rule, as two numbers. The medias and stacks used to be listed row by row
  // underneath; the count is what a publisher about to change a rule is actually asking for, and the
  // list of names answered a different question at the cost of most of the flyout.
  const stackCount = useMemo(() => Object.values(stacksUse ?? {}).reduce((total, stacks) => total + stacks.length, 0), [stacksUse])
  const mediaCount = Object.keys(stacksUse ?? {}).length

  return (
    <Flyout.Content>
      <Flyout.Title>{name} - details</Flyout.Title>
      <Flyout.Body>
        <div className={'flex flex-col gap-6'}>
          <section className={'flex flex-col gap-2'}>
            <h3 className={'m-0 font-medium'}>Settings</h3>
            <dl className={'m-0 grid grid-cols-[auto_1fr] gap-x-6 gap-y-2'}>
              {details.map(({ label, value }) => (
                <div key={label} className={'col-span-2 grid grid-cols-subgrid'}>
                  <dt className={'text-slate-grey'}>{label}</dt>
                  <dd className={'m-0'}>{value}</dd>
                </div>
              ))}
            </dl>
          </section>

          {/* Rendered even when empty — "used by nothing" is the one answer a publisher about to change a
              rule most wants, and hiding the section withholds it. */}
          <section className={'flex flex-col gap-2'}>
            <h3 className={'m-0 font-medium'}>
              Used by {mediaCount} media{mediaCount === 1 ? '' : 's'}
              {stackCount > 0 && <span className={'font-normal text-slate-500'}>{` · ${stackCount} stack${stackCount === 1 ? '' : 's'}`}</span>}
            </h3>
            {stackCount === 0 && <span className={'text-slate-grey'}>Not used by any stack.</span>}
          </section>
        </div>
      </Flyout.Body>
      <Flyout.Actions>
        {onEdit ? (
          <Button variant={'secondary'} iconName={'edit'} onClick={onEdit} trackingLabel={editTrackingLabel}>
            Edit rule
          </Button>
        ) : (
          /* Said rather than shown disabled: a greyed button invites a hover to find out why, and the reason
             is one short sentence. */
          <span className={'text-slate-grey'}>{editDisabledReason}</span>
        )}
      </Flyout.Actions>
    </Flyout.Content>
  )
}
